import useTouchStart from 'hooks/useTouchStart'
import { triggerEventTarget, updateContentHeight } from 'libraries/front-end-core/helpers/document'
import { dismissPopover, triggerPopoverContent, triggerPopoverItem } from 'services/OverlayService'
import { notifyRender, Scroll, Touch } from 'libraries/front-end-core/services/WindowService'
import { Subject } from 'rxjs'
import { startWith, pairwise, debounceTime } from 'rxjs/operators'
import React, { useEffect, useRef } from 'react'
import { updatePageView } from 'libraries/front-end-core/services/PageStateService'
import { mapKeys, startsWith } from 'libraries/lodash'
import notificationToast from 'helpers/notificationToast'
import LinkIcon from '@mui/icons-material/Link'
import { toast } from 'react-toastify'
import InfoIcon from '@mui/icons-material/Info'

const usePage = (setPageView, pageView, pageId, toggleFullscreenOverlay) => {
  const containerRef = useRef()

  const handleSwipeLeft = () => {
    setPageView(pageView === 'right' ? 'right' : 'main')
  }

  const handleSwipeRight = () => {
    const isRight = pageView === 'right'
    setPageView(isRight ? 'main' : 'left')
    return !isRight
  }

  const { handleTouchStart } = useTouchStart(handleSwipeLeft, handleSwipeRight)

  const handleTouch = ([previous, next]) => {
    const hit = triggerEventTarget(next.target, {
      '[data-hover-popover-item]': triggerPopoverItem({ name: 'hoverPopoverItem', persist: false }),
      '[data-hover-popover-content]': triggerPopoverContent({ name: 'hoverPopoverContent', persist: false })
    })

    hit && (!previous || previous.target !== next.target) && next.preventDefault()
  }

  const handleMouseMove = (evtTarget) => {
    triggerEventTarget(evtTarget, {
      '[data-hover-popover-item]': triggerPopoverItem({ name: 'hoverPopoverItem', persist: false }),
      '[data-hover-popover-content]': triggerPopoverContent({ name: 'hoverPopoverContent', persist: false })
    }) || dismissPopover(evtTarget)
  }

  const subscriptions = []
  subscriptions.push(Touch.pipe(startWith(null), pairwise()).subscribe(handleTouch))

  const MouseMove = new Subject()
  subscriptions.push(MouseMove.pipe(debounceTime(20)).subscribe(handleMouseMove))

  useEffect(() => {
    updateContentHeight()
    updatePageView(pageView, pageId)
    setPageView(pageView)
    notifyRender()

    return () => {
      subscriptions.forEach(s => s.unsubscribe())
      toggleFullscreenOverlay(null)
    }
  }, [])

  useEffect(() => {
    updatePageView(pageView, pageId)
    setPageView(pageView)
  }, [pageView, pageId])

  const handleScroll = (evt) => {
    const { currentTarget } = evt
    Scroll.next({
      scrollMain: currentTarget.scrollTop,
      scrollHeight: currentTarget.scrollHeight,
      viewHeight: containerRef.current.getBoundingClientRect().height,
      pageId
    })
  }

  const handleClick = (evt) => {
    const matched = triggerEventTarget(evt.target, {
      '[data-popover-item]': triggerPopoverItem({ name: 'popoverItem' }),
      '[data-popover-content]': triggerPopoverContent({ name: 'popoverContent' }),
      '[data-notification-message]': triggerNotification
    })

    if (!matched && !evt.target.closest('.popover-container')) {
      dismissPopover(evt.target)
    }
  }

  const triggerNotification = (el) => {
    const KEY = 'notification'
    const props = mapKeys(el.dataset, (v, k) => startsWith(k, KEY) && k.substr(KEY.length).toLowerCase())
    const { message, icon } = props
    switch (icon) {
      case 'icon-clipboard-outline':
        notificationToast(<LinkIcon />, toast.TYPE.SUCCESS, message)
        break
      default:
        notificationToast(<InfoIcon />, toast.TYPE.INFO, message)
        break
    }
  }

  return { containerRef, handleTouchStart, handleScroll, handleClick, triggerNotification }
}

export default usePage
